import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, IconButton, TextField,
    InputAdornment, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
    ArrowBackOutlined, SearchOutlined, FavoriteBorderOutlined,
    FavoriteOutlined, LockOutlined, NorthEastOutlined,
    VolunteerActivismOutlined, WaterDropOutlined, ChildCareOutlined,
    HomeOutlined, FavoriteRounded,
} from '@mui/icons-material';

/* ── Brand tokens ──────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    gold:    '#F5C842',
    orange:  '#FF9500',
    blue:    '#007AFF',
    red:     '#FF4D6A',
};

/* ── Category config ───────────────────────────────────────────── */
const CATEGORIES = [
    { label: 'All',           color: B.purple },
    { label: 'Water',         color: B.blue   },
    { label: 'Children',      color: B.green  },
    { label: 'Disaster',      color: B.orange },
    { label: 'Housing',       color: B.gold   },
    { label: 'Animals',       color: B.red    },
];

/* ── Charity data ──────────────────────────────────────────────── */
interface Charity {
    id: number;
    name: string;
    category: string;
    description: string;
    raised: number;
    goal: number;
    donors: number;
    color: string;
    Icon: React.ElementType;
}

const CHARITIES: Charity[] = [
    {
        id: 1,
        name: 'WaterAid Nigeria',
        category: 'Water',
        description: 'Providing clean water and sanitation access to rural communities across Nigeria.',
        raised: 2_450_000,
        goal:   5_000_000,
        donors: 1_240,
        color: B.blue,
        Icon: WaterDropOutlined,
    },
    {
        id: 2,
        name: 'Save the Children Nigeria',
        category: 'Children',
        description: 'Supporting education, health, and protection programmes for children in need.',
        raised: 3_800_000,
        goal:   6_000_000,
        donors: 2_015,
        color: B.green,
        Icon: ChildCareOutlined,
    },
    {
        id: 3,
        name: 'Nigerian Red Cross',
        category: 'Disaster',
        description: 'Disaster response and humanitarian aid across all 36 Nigerian states.',
        raised: 1_750_000,
        goal:   4_000_000,
        donors: 870,
        color: B.orange,
        Icon: VolunteerActivismOutlined,
    },
    {
        id: 4,
        name: 'Lifeline Shelter',
        category: 'Housing',
        description: 'Providing food and shelter to vulnerable families across Lagos and Abuja.',
        raised: 900_000,
        goal:   2_500_000,
        donors: 430,
        color: B.gold,
        Icon: HomeOutlined,
    },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

/* ── Donate amount options ─────────────────────────────────────── */
const AMOUNTS = [500, 1_000, 2_500, 5_000, 10_000];

/* ─────────────────────────────────────────────────────────────── */

const DonatePage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [search,    setSearch]    = useState('');
    const [category,  setCategory]  = useState('All');
    const [favorites, setFavorites] = useState<number[]>([]);
    const [selected,  setSelected]  = useState<Charity | null>(null);
    const [amount,    setAmount]    = useState<number | null>(null);
    const [custom,    setCustom]    = useState('');
    const [success,   setSuccess]   = useState(false);

    const pageBg    = isDark ? B.deep : B.lavender;
    const surfaceBg = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr= `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd= `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const glass     = { background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };
    const textPri   = isDark ? '#fff' : B.ink;
    const textMid   = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC  = alpha(isDark ? '#fff' : B.dark, .08);

    const toggleFav = (id: number) =>
        setFavorites(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

    const filtered = CHARITIES.filter(c => {
        const matchCat = category === 'All' || c.category === category;
        const matchQ   = c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.category.toLowerCase().includes(search.toLowerCase());
        return matchCat && matchQ;
    });

    const donateAmount = amount ?? (custom ? parseInt(custom) : 0);

    /* ── Donate confirmation view ── */
    if (success && selected) {
        return (
            <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                <Box sx={{ textAlign: 'center', px: 4 }}>
                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: alpha(B.green, .12), border: `2px solid ${alpha(B.green, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                        <FavoriteRounded sx={{ color: B.green, fontSize: 38 }} />
                    </Box>
                    <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.7rem', mb: .75 }}>
                        Thank you!
                    </Typography>
                    <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .4 }}>
                        Your donation of
                    </Typography>
                    <Typography sx={{ color: B.green, fontFamily: "'Instrument Serif', serif", fontSize: '1.5rem', fontWeight: 700, mb: .4 }}>
                        {fmt(donateAmount)}
                    </Typography>
                    <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>
                        to <strong>{selected.name}</strong> was successful.
                    </Typography>
                    <Typography sx={{ color: textFaint, fontSize: '.75rem', mb: 4 }}>
                        This donation may contribute to your NG Credit Score.
                    </Typography>
                    <Box
                        onClick={() => { setSuccess(false); setSelected(null); setAmount(null); setCustom(''); }}
                        sx={{ ...glass, px: 3, py: 1.5, display: 'inline-block', cursor: 'pointer', borderRadius: '14px', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, boxShadow: `0 8px 24px ${alpha(B.purple, .35)}` }}
                    >
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.9rem' }}>Donate Again</Typography>
                    </Box>
                    <Box sx={{ mt: 1.5 }}>
                        <Typography onClick={() => navigate(-1)} sx={{ color: textFaint, fontSize: '.8rem', cursor: 'pointer', fontWeight: 600 }}>
                            Back to Menu
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    /* ── Donate flow view ── */
    if (selected) {
        const { Icon } = selected;
        const pct = Math.round((selected.raised / selected.goal) * 100);
        return (
            <Box sx={{ minHeight: '100vh', background: pageBg, pb: 12, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                {/* Hero */}
                <Box sx={{
                    background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${alpha(selected.color, .7)} 100%)`,
                    mt: { xs: '-76px', sm: 0 }, pt: { xs: '80px', sm: '28px' }, pb: { xs: 4, sm: 14 },
                    px: 3, position: 'relative', overflow: 'hidden',
                }}>
                    <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(selected.color, .2)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                        <IconButton size="small" onClick={() => setSelected(null)}
                                    sx={{ color: alpha('#fff', .6), background: alpha('#fff', .08), '&:hover': { background: alpha('#fff', .16) } }}>
                            <ArrowBackOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: alpha(selected.color, .18), border: `1px solid ${alpha(selected.color, .35)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon sx={{ color: selected.color, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>{selected.category}</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.95rem' }}>{selected.name}</Typography>
                        </Box>
                    </Box>
                </Box>

                <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
                    {/* Progress card */}
                    <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                        <Typography sx={{ color: textMid, fontSize: '.78rem', lineHeight: 1.7, mb: 2 }}>{selected.description}</Typography>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Box>
                                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.2rem' }}>{fmt(selected.raised)}</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.62rem' }}>raised of {fmt(selected.goal)}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ color: selected.color, fontFamily: "'Instrument Serif', serif", fontSize: '1.2rem' }}>{pct}%</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.62rem' }}>{selected.donors.toLocaleString()} donors</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ height: 6, borderRadius: 4, background: alpha(isDark ? '#fff' : B.dark, .08) }}>
                            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${B.purple}, ${selected.color})`, transition: 'width 1s cubic-bezier(.16,1,.3,1)' }} />
                        </Box>
                    </Box>

                    {/* Amount selection */}
                    <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', mb: 1.75 }}>Choose an Amount</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                            {AMOUNTS.map(a => {
                                const active = amount === a;
                                return (
                                    <Box key={a} onClick={() => { setAmount(a); setCustom(''); }} sx={{
                                        py: 1.1, borderRadius: '12px', textAlign: 'center', cursor: 'pointer',
                                        background: active ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : alpha(isDark ? '#fff' : B.dark, .05),
                                        border: `1px solid ${active ? 'transparent' : alpha(isDark ? '#fff' : B.dark, .08)}`,
                                        boxShadow: active ? `0 4px 16px ${alpha(B.purple, .32)}` : 'none',
                                        transition: 'all .18s',
                                    }}>
                                        <Typography sx={{ color: active ? '#fff' : textMid, fontWeight: 700, fontSize: '.8rem' }}>
                                            ₦{a.toLocaleString()}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                        <TextField
                            fullWidth
                            placeholder="Custom amount"
                            value={custom}
                            onChange={e => { setCustom(e.target.value.replace(/\D/g, '')); setAmount(null); }}
                            variant="standard"
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Typography sx={{ color: textFaint, fontSize: '.9rem' }}>₦</Typography></InputAdornment>,
                                sx: {
                                    color: textPri, fontWeight: 700, fontSize: '1.1rem',
                                    borderBottom: `2px solid ${custom ? B.purple : alpha(isDark ? '#fff' : B.dark, .15)}`,
                                    '&:before, &:after': { display: 'none' },
                                },
                            }}
                        />
                    </Box>

                    {/* Credit score note */}
                    <Box sx={{ ...glass, p: 1.75, mb: 2.5, border: `1px solid ${alpha(B.green, .18)}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: alpha(B.green, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <NorthEastOutlined sx={{ color: B.green, fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ color: textMid, fontSize: '.76rem', lineHeight: 1.6 }}>
                            Donating to healthy causes can improve your <span style={{ color: B.green, fontWeight: 700 }}>NG Credit Score</span>.
                        </Typography>
                    </Box>

                    {/* CTA */}
                    <Box
                        onClick={() => donateAmount > 0 && setSuccess(true)}
                        sx={{
                            background: donateAmount > 0
                                ? `linear-gradient(135deg, ${B.purple}, ${B.light})`
                                : alpha(B.mid, .4),
                            borderRadius: '14px', py: 1.75, textAlign: 'center', cursor: donateAmount > 0 ? 'pointer' : 'default',
                            boxShadow: donateAmount > 0 ? `0 8px 24px ${alpha(B.purple, .35)}` : 'none',
                            transition: 'all .2s',
                        }}
                    >
                        <Typography sx={{ color: donateAmount > 0 ? '#fff' : alpha('#fff', .3), fontWeight: 700, fontSize: '.95rem' }}>
                            {donateAmount > 0 ? `Donate ${fmt(donateAmount)}` : 'Select an Amount'}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                        <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                        <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                            SECURE DONATION · NG CREDIT FINANCIAL SERVICES
                        </Typography>
                    </Box>
                </Container>
            </Box>
        );
    }

    /* ── Main listing view ── */
    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 12, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 }, pt: { xs: '80px', sm: '28px' }, pb: { xs: 4, sm: 14 },
                px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.red, .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.green, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <IconButton size="small" onClick={() => navigate(-1)}
                                    sx={{ color: alpha('#fff', .6), background: alpha('#fff', .08), '&:hover': { background: alpha('#fff', .16) } }}>
                            <ArrowBackOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                                Donate
                            </Typography>
                        </Box>
                    </Box>
                    {/* heart badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.red, .12), border: `1px solid ${alpha(B.red, .28)}`, borderRadius: 20, px: 1.5, py: .6 }}>
                        <FavoriteRounded sx={{ color: B.red, fontSize: 12 }} />
                        <Typography sx={{ color: B.red, fontSize: '.66rem', fontWeight: 700, letterSpacing: '.04em' }}>
                            {favorites.length} SAVED
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Search ── */}
                <Box sx={{ ...glass, display: 'flex', alignItems: 'center', gap: 1, px: 2, mb: 2 }}>
                    <SearchOutlined sx={{ color: textFaint, fontSize: 20, flexShrink: 0 }} />
                    <TextField
                        fullWidth
                        placeholder="Search causes or categories…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        variant="standard"
                        InputProps={{ disableUnderline: true, sx: { color: textPri, fontSize: '.88rem', py: 1.5 } }}
                    />
                </Box>

                {/* ── Category pills ── */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, overflowX: 'auto', pb: .5, '&::-webkit-scrollbar': { display: 'none' } }}>
                    {CATEGORIES.map(({ label, color }) => {
                        const active = category === label;
                        return (
                            <Box key={label} onClick={() => setCategory(label)} sx={{
                                px: 1.5, py: .6, borderRadius: 20, flexShrink: 0, cursor: 'pointer',
                                background: active ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : alpha(isDark ? '#fff' : B.dark, .05),
                                border: `1px solid ${active ? 'transparent' : alpha(isDark ? '#fff' : B.dark, .08)}`,
                                boxShadow: active ? `0 4px 12px ${alpha(B.purple, .28)}` : 'none',
                                transition: 'all .18s',
                            }}>
                                <Typography sx={{ color: active ? '#fff' : textMid, fontWeight: 600, fontSize: '.72rem' }}>
                                    {label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Credit score callout ── */}
                <Box sx={{ ...glass, p: 1.75, mb: 2.5, border: `1px solid ${alpha(B.green, .16)}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: alpha(B.green, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <NorthEastOutlined sx={{ color: B.green, fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ color: textMid, fontSize: '.76rem', lineHeight: 1.6 }}>
                        Donating to healthy causes can improve your <span style={{ color: B.green, fontWeight: 700 }}>NG Credit Score</span>.
                    </Typography>
                </Box>

                {/* ── Charity cards ── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {filtered.length === 0 && (
                        <Box sx={{ ...glass, p: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: textFaint, fontSize: '.85rem' }}>No causes match your search.</Typography>
                        </Box>
                    )}

                    {filtered.map(charity => {
                        const { Icon } = charity;
                        const pct = Math.round((charity.raised / charity.goal) * 100);
                        const isFav = favorites.includes(charity.id);

                        return (
                            <Box key={charity.id} sx={{ ...glass, overflow: 'hidden', transition: 'all .18s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(charity.color, .14)}` } }}>
                                {/* Colour accent bar */}
                                <Box sx={{ height: 3, background: `linear-gradient(90deg, ${charity.color}, ${alpha(charity.color, .2)})` }} />

                                <Box sx={{ p: 2.25 }}>
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: alpha(charity.color, .1), border: `1px solid ${alpha(charity.color, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <Icon sx={{ color: charity.color, fontSize: 20 }} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.015em' }}>
                                                    {charity.name}
                                                </Typography>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: .25, px: .85, py: .2, borderRadius: 8, background: alpha(charity.color, .08) }}>
                                                    <Typography sx={{ color: charity.color, fontSize: '.6rem', fontWeight: 700 }}>{charity.category}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <IconButton size="small" onClick={() => toggleFav(charity.id)} sx={{ color: isFav ? B.red : textFaint, p: .75 }}>
                                            {isFav
                                                ? <FavoriteOutlined sx={{ fontSize: 18 }} />
                                                : <FavoriteBorderOutlined sx={{ fontSize: 18 }} />
                                            }
                                        </IconButton>
                                    </Box>

                                    <Typography sx={{ color: textMid, fontSize: '.76rem', lineHeight: 1.65, mb: 1.75 }}>
                                        {charity.description}
                                    </Typography>

                                    {/* Progress */}
                                    <Box display="flex" justifyContent="space-between" mb={.75}>
                                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.78rem' }}>
                                            {fmt(charity.raised)}
                                        </Typography>
                                        <Typography sx={{ color: charity.color, fontSize: '.72rem', fontWeight: 700 }}>
                                            {pct}% · {charity.donors.toLocaleString()} donors
                                        </Typography>
                                    </Box>
                                    <Box sx={{ height: 5, borderRadius: 4, background: alpha(isDark ? '#fff' : B.dark, .08), mb: 1.75 }}>
                                        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${B.purple}, ${charity.color})`, transition: 'width 1s cubic-bezier(.16,1,.3,1)' }} />
                                    </Box>

                                    <Divider sx={{ borderColor: dividerC, mb: 1.75 }} />

                                    <Box
                                        onClick={() => setSelected(charity)}
                                        sx={{
                                            py: 1.2, borderRadius: '12px', textAlign: 'center', cursor: 'pointer',
                                            background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                            boxShadow: `0 4px 16px ${alpha(B.purple, .3)}`,
                                            transition: 'all .18s',
                                            '&:hover': { boxShadow: `0 8px 24px ${alpha(B.purple, .4)}` },
                                        }}
                                    >
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.85rem' }}>Donate Now</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Footer */}
                <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default DonatePage;